import React from 'react';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import DateRangePicker from 'components/DateControls/DateRangePicker';
import { Dropdown, DropdownToggle } from 'components/Dropdown2';
import { SvgArrow } from 'components/Svgs';
import { bem } from 'helpers/class-name';

import { earliestReportDate, latestReportDate, shiftRange } from './SkuOrderReports.filters';
import messages from './SkuOrderReports.messages';
import './SkuReportDateRangeFilter.scss';

const { block, element } = bem('sku-report-date-range-filter');

const SkuReportDateRangeFilter = ({ value, onChange }) => {
  const intl = useIntl();
  const formatDay = date => intl.formatDate(date, { month: 'short', day: 'numeric' });

  const previousRange = shiftRange(value, -1);
  const nextRange = shiftRange(value, 1);

  const step = (event, range, closeMenu) => {
    event.stopPropagation();
    if (range !== value) onChange(range);
    closeMenu();
  };

  return (
    <Dropdown attachment="left" className={block()}>
      {({ closeMenu }) => (
        <>
          <DropdownToggle label={messages.dateRangeLabel} hideCaret>
            <div className={element('control')}>
              <span className={element('range')}>{`${formatDay(value.from)} – ${formatDay(value.to)}`}</span>
              <div className={element('steppers')}>
                <button
                  aria-label={intl.formatMessage(messages.previousPeriod)}
                  className={element('stepper')}
                  disabled={previousRange === value}
                  onClick={event => step(event, previousRange, closeMenu)}
                  tabIndex="-1"
                  type="button">
                  <SvgArrow direction="left" theme={previousRange === value ? 'disabled' : 'dark'} variant="chevron" />
                </button>
                <button
                  aria-label={intl.formatMessage(messages.nextPeriod)}
                  className={element('stepper')}
                  disabled={nextRange === value}
                  onClick={event => step(event, nextRange, closeMenu)}
                  tabIndex="-1"
                  type="button">
                  <SvgArrow direction="right" theme={nextRange === value ? 'disabled' : 'dark'} variant="chevron" />
                </button>
              </div>
            </div>
          </DropdownToggle>
          <DateRangePicker
            defaultRange={value}
            modifiers={{ disabled: { before: earliestReportDate(), after: latestReportDate() } }}
            onApply={range => {
              if (range?.from && range?.to) onChange(range);
              closeMenu();
            }}
            onCancel={closeMenu}
          />
        </>
      )}
    </Dropdown>
  );
};

SkuReportDateRangeFilter.propTypes = {
  value: PropTypes.shape({
    from: PropTypes.instanceOf(Date).isRequired,
    to: PropTypes.instanceOf(Date).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SkuReportDateRangeFilter;
